import { communicationService, eventsRepository } from '@ad-engine/communication';
import { context, externalLogger, targetingService, utils } from '@ad-engine/core';

const logGroup = 'IntentIQ';
const PREBID_BIDDING_PLATFORM_ID = 1;

export class IntentIQ {
	private loadPromise: Promise<void> | undefined;
	private loaded = false;
	private fandomId = {
		default: 1187275693,
		v6_1: 339079598,
	};
	private intentIqObjectName = {
		default: 'IntentIqObject',
		v6_1: 'IntentIqObject61',
	};
	private intentIqObject: IntentIqObject | undefined;

	preloadScript(): Promise<void> {
		if (!this.isEnabled()) {
			return Promise.resolve();
		}

		communicationService.emit(eventsRepository.PARTNER_LOAD_STATUS, {
			status: 'intentiq_started',
		});

		if (this.loadPromise) {
			return this.loadPromise;
		}

		const intentIQScriptUrl = `//script.wikia.nocookie.net/fandom-ae-assets/intentiq/
			${this.getIntentIqVersion()}/IIQUniversalID.js`;

		this.loadPromise = utils.scriptLoader.loadScript(intentIQScriptUrl, true, 'first').then(() => {
			this.loaded = true;
			utils.logger(logGroup, 'loaded');
		});

		return this.loadPromise;
	}

	async initialize(pbjs: Pbjs): Promise<void> {
		if (!this.isEnabled()) {
			utils.logger(logGroup, 'disabled');
			return;
		}

		if (!this.loaded) {
			await this.preloadScript();
			await new utils.WaitFor(
				() => window[this.getIntentIqObjectName()] !== undefined,
				10,
				10,
			).until();
		}

		if (!this.intentIqObject) {
			const intentIqConfig = this.getIntentIqConfig(pbjs);

			utils.logger(logGroup, 'config', intentIqConfig);
			utils.logger(
				logGroup,
				`v${this.getIntentIqVersion()}, window.${this.getIntentIqObjectName()}`,
			);

			return new Promise((resolve) => {
				if (this.is61Version()) {
					if (!window.IntentIqObject61) {
						utils.warner(logGroup, 'IntentIqObject61 not loaded!');
						resolve();
						return;
					}

					this.intentIqObject = new window.IntentIqObject61({
						...intentIqConfig,
						callback: (data, abTestGroup) => {
							utils.logger(logGroup, 'got data', data);

							abTestGroup ??= 'U';
							this.setTargetingAndEmitEvents(abTestGroup);

							resolve();
						},
					});
				} else {
					if (!window.IntentIqObject) {
						utils.warner(logGroup, 'IntentIqObject not loaded!');
						resolve();
						return;
					}

					this.intentIqObject = new window.IntentIqObject({
						...intentIqConfig,
						callback: (data) => {
							utils.logger(logGroup, 'got data', data);
							resolve();
						},
					});
					const abTestGroup =
						this.intentIqObject?.intentIqConfig?.abTesting?.currentTestGroup ?? 'U';
					this.setTargetingAndEmitEvents(abTestGroup);
				}
			});
		}
	}

	private setTargetingAndEmitEvents(abTestGroup: string) {
		targetingService.set('intent_iq_group', abTestGroup);
		communicationService.emit(eventsRepository.PARTNER_LOAD_STATUS, {
			status: 'intentiq_loaded',
		});
		communicationService.emit(eventsRepository.INTENT_IQ_GROUP_OBTAINED, {
			abTestGroup,
		});
	}

	private is61Version = () => this.getIntentIqVersion() === '6.1';

	private getIntentIqObjectName = () =>
		this.is61Version() ? this.intentIqObjectName.v6_1 : this.intentIqObjectName.default;

	private getIntentIqVersion = () => {
		const result = context.get('bidders.prebid.intentIQVersion');
		return result === undefined ? undefined : result.toString();
	};

	private getIntentIqConfig(pbjs: Pbjs) {
		const domainName = window.location.hostname.includes('.fandom.com')
			? 'fandom.com'
			: window.location.hostname;
		const timeoutInMilis = context.get('bidders.prebid.intentIQTimeout');

		const intentIqConfig: IntentIQConfig = {
			partner: this.is61Version() ? this.fandomId.v6_1 : this.fandomId.default,
			pbjs,
			timeoutInMillis: timeoutInMilis,
			ABTestingConfigurationSource: this.is61Version() ? 'IIQServer' : 'percentage',
			manualWinReportEnabled: true,
			domainName,
			vrBrowserBlackList: ['chrome'],
		};

		if (!this.is61Version()) {
			intentIqConfig.abPercentage = 97;
		}

		return intentIqConfig;
	}

	async reportPrebidWin(bid: PrebidBidResponse): Promise<void> {
		if (!this.isEnabled() || !this.intentIqObject || utils.client.isChromeBrowser()) {
			return;
		}

		const data: IntentIQReportData = {
			biddingPlatformId: PREBID_BIDDING_PLATFORM_ID,
			bidderCode: bid.bidderCode,
			prebidAuctionId: bid.auctionId,
			cpm: bid.cpm,
			currency: bid.currency,
			originalCpm: bid.originalCpm,
			originalCurrency: bid.originalCurrency,
			status: bid.status,
			placementId: bid.adUnitCode,
		};

		utils.logger(logGroup, 'reporting prebid win', data);

		this.intentIqObject.reportExternalWin(data);

		externalLogger.log('intentiq report', { report: JSON.stringify(data) });
	}

	isEnabled(): boolean {
		return (
			context.get('bidders.prebid.intentIQ') &&
			context.get('options.trackingOptIn') &&
			!context.get('options.optOutSale') &&
			!utils.isCoppaSubject()
		);
	}
}

export const intentIQ = new IntentIQ();
